import {
  getDaysRemainingAt as calculateDaysRemaining,
  getTimelineStatusAt as getTimelineStatus,
} from '@/helpers/ssr/dates'

import type {
  FederalContract,
  FederalNotice,
  LinkedAwardSummary,
  LinkedAwardsResponse,
} from '@/types/federal-contract/FederalContract'

import type { BadgeVariant } from '../../common/BadgeV2'
import {
  type NormalizedFile,
  formatCurrency,
  formatDate,
  normalizeDepartmentName,
  normalizeFiles,
  sortNoticesByDate,
} from '../../common/transforms'

// ============================================================================
// Types
// ============================================================================

export interface TimelineEvent {
  id?: string
  date: string
  label: string
  status: 'past' | 'current' | 'future'
  variant?: 'default' | 'amendment' | 'deadline' | 'award'
  noticeId?: string
}

export interface NormalizedContractData {
  // Contract ID
  id: string

  // Header
  title: string
  subtitle: string // Department name
  solicitationNumber: string | null
  badges: Array<{
    label: string
    variant: BadgeVariant
  }>

  // Key Stats
  stats: {
    department: string | null
    subTier: string | null
    office: string | null
    noticeType: string | null
    responseDeadline: string | null
    responseDeadlineRaw: string | null
    daysRemaining: number | null
    setAsideCode: string | null
    setAsideDescription: string | null
  }

  // Classification Codes
  codes: {
    naicsCode: string | null
    naicsIndustry: string | null
    pscCode: string | null
    pscIndustry: string | null
  }

  // Place of Performance
  placeOfPerformance: {
    street: string | null
    city: string | null
    state: string | null
    zip: string | null
    country: string | null
    formatted: string | null
  }

  // Contact Info
  contact: {
    title: string | null
    name: string | null
    email: string | null
    phone: string | null
    fax: string | null
  }

  // Secondary Contact
  secondaryContact: {
    title: string | null
    name: string | null
    email: string | null
    phone: string | null
    fax: string | null
  } | null

  // Timeline
  timeline: TimelineEvent[]

  // Notices
  notices: FederalNotice[]
  latestNotice: FederalNotice

  // Award Notices (from SAM.gov)
  awardNotices: Array<{
    id: string
    awardNumber: string | null
    awardDate: string | null
    awardAmount: number | null
    awardee: string | null
  }>

  // Content
  description: string | null
  aiSummary: string | null

  // Files
  files: NormalizedFile[]

  // External Links
  externalLink: string | null
}

export interface NormalizedLinkedAward {
  id: number
  generatedId: string
  piid: string | null
  fain: string | null
  category: string | null
  typeDescription: string | null
  description: string | null
  totalObligation: number | null
  recipientName: string | null
  recipientUei: string | null
  recipientHash: string | null
  recipientLevels: string[]
  parentUei: string | null
  agencyName: string | null
  subtierName: string | null
  actionDate: string | null
  dateSigned: string | null
  startDate: string | null
  endDate: string | null
  popCity: string | null
  popState: string | null
  popCountry: string | null
}

export interface NormalizedLinkedAwardData {
  // Summary
  totalObligated: number | null
  awardCount: number

  // Awards
  awards: Array<NormalizedLinkedAward>
}

/**
 * Unified award notice that can represent both SAM.gov award notices
 * and USAspending linked awards
 */
export interface UnifiedAwardNotice {
  id: string
  source: 'sam' | 'usaspending'
  awardNumber: string | null
  awardDate: string | null
  awardAmount: number | null
  awardee: string | null
  // USAspending-specific fields (null for SAM.gov awards)
  generatedId: string | null
  typeDescription: string | null
  recipientHash: string | null
  recipientLevels: string[]
  parentUei: string | null
  agencyName: string | null
}

// ============================================================================
// Domain-Specific Utilities
// ============================================================================

/**
 * Map notice type to badge variant
 */
export function getNoticeTypeVariant(type: string | undefined): BadgeVariant {
  if (!type) return 'outline'
  const typeLower = type.toLowerCase()
  if (typeLower.includes('presolicitation')) return 'neutral'
  if (typeLower.includes('solicitation') || typeLower.includes('combined'))
    return 'success'
  if (typeLower.includes('award')) return 'info'
  if (typeLower.includes('modification') || typeLower.includes('amendment'))
    return 'warning'
  if (typeLower.includes('cancel')) return 'neutral'
  return 'outline'
}

/**
 * Map set-aside code to badge variant
 */
function getSetAsideVariant(code: string | undefined): BadgeVariant {
  if (!code || code === 'NONE') return 'neutral'
  // Small business set-asides
  if (code.includes('SB') || code.includes('SBA')) return 'success'
  // 8(a)
  if (code.includes('8A')) return 'info'
  // HUBZone
  if (code.includes('HZC') || code.includes('HUB')) return 'warning'
  // Service-Disabled Veteran
  if (code.includes('SDVO')) return 'info'
  // Women-owned
  if (code.includes('WO') || code.includes('EDWO')) return 'info'
  return 'outline'
}

/**
 * Format place of performance into single string
 */
function formatPlaceOfPerformance(
  notice: FederalNotice | undefined,
): string | null {
  if (!notice) return null
  const parts = [
    notice.place_of_performance_city,
    notice.place_of_performance_state,
    notice.place_of_performance_country !== 'USA' &&
    notice.place_of_performance_country !== 'US'
      ? notice.place_of_performance_country
      : null,
  ].filter(Boolean)
  return parts.length > 0 ? parts.join(', ') : null
}

// ============================================================================
// Main Transform
// ============================================================================

/**
 * Normalize raw federal contract data into a format suitable for DisplayV2 components
 */
export function normalizeContractData(
  contract: FederalContract,
  referenceMs: number,
): NormalizedContractData {
  // Sort notices by posted_date descending (newest first)
  const sortedNotices = sortNoticesByDate(contract.notices)

  const latestNotice = sortedNotices[0]

  // Build badges
  const badges: Array<{ label: string; variant: BadgeVariant }> = [
    { label: 'Contract', variant: 'info' },
  ]

  if (latestNotice.notice_type) {
    badges.push({
      label: latestNotice.notice_type,
      variant: getNoticeTypeVariant(latestNotice.notice_type),
    })
  }

  if (latestNotice.set_aside_code && latestNotice.set_aside_code !== 'NONE') {
    badges.push({
      label: latestNotice.set_aside_description || latestNotice.set_aside_code,
      variant: getSetAsideVariant(latestNotice.set_aside_code),
    })
  }

  // Build timeline
  const timeline: TimelineEvent[] = []

  // Add all notice postings to timeline
  sortedNotices.forEach((notice, idx) => {
    const isOriginal = idx === sortedNotices.length - 1
    const noticeNumber = sortedNotices.length - idx

    let label: string
    if (isOriginal) {
      label = 'Original Notice'
    } else if (notice.notice_type) {
      label = notice.notice_type
    } else {
      label = `Notice ${noticeNumber}`
    }

    timeline.push({
      id: `notice-${notice.id}`,
      date: notice.posted_date,
      label,
      status: getTimelineStatus(notice.posted_date, referenceMs),
      variant: isOriginal ? 'default' : 'amendment',
      noticeId: notice.id,
    })
  })

  // Add response deadline
  if (latestNotice.response_deadline) {
    timeline.push({
      id: 'response-deadline',
      date: latestNotice.response_deadline,
      label: 'Response Deadline',
      status: getTimelineStatus(
        latestNotice.response_deadline,
        referenceMs,
        true,
      ),
      variant: 'deadline',
    })
  }

  // Add archive date
  if (latestNotice.archive_date) {
    timeline.push({
      id: 'archive-date',
      date: latestNotice.archive_date,
      label: 'Archive Date',
      status: getTimelineStatus(latestNotice.archive_date, referenceMs),
      variant: 'default',
    })
  }

  // Add award notices to timeline
  contract.award_notices?.forEach((awardNotice, idx) => {
    if (awardNotice.award_date) {
      timeline.push({
        id: `award-${awardNotice.id}`,
        date: awardNotice.award_date,
        label: awardNotice.awardee
          ? `Awarded to ${awardNotice.awardee}`
          : `Award ${idx + 1}`,
        status: getTimelineStatus(awardNotice.award_date, referenceMs),
        variant: 'award',
      })
    }
  })

  // Sort timeline chronologically
  timeline.sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  )

  // Calculate days remaining until deadline
  const daysRemaining = calculateDaysRemaining(
    latestNotice.response_deadline,
    referenceMs,
  )

  // Normalize files
  const files = normalizeFiles(contract.files)

  // Normalize award notices
  const awardNotices =
    contract.award_notices?.map((an) => ({
      id: an.id,
      awardNumber: an.award_number || null,
      awardDate: an.award_date || null,
      awardAmount: an.award_amount ?? null,
      awardee: an.awardee || null,
    })) ?? []

  // Build secondary contact if available
  const secondaryContact =
    latestNotice.secondary_contact_full_name ||
    latestNotice.secondary_contact_email
      ? {
          title: latestNotice.secondary_contact_title || null,
          name: latestNotice.secondary_contact_full_name || null,
          email: latestNotice.secondary_contact_email || null,
          phone: latestNotice.secondary_contact_phone || null,
          fax: latestNotice.secondary_contact_fax || null,
        }
      : null

  return {
    id: contract.id,

    title: latestNotice.title || 'Federal Contract Opportunity',
    subtitle:
      normalizeDepartmentName(latestNotice.department) || 'Federal Agency',
    solicitationNumber: latestNotice.solicitation_number || null,
    badges,

    stats: {
      department: normalizeDepartmentName(latestNotice.department),
      subTier: latestNotice.sub_tier || null,
      office: latestNotice.office || null,
      noticeType: latestNotice.notice_type || null,
      responseDeadline: formatDate(latestNotice.response_deadline),
      responseDeadlineRaw: latestNotice.response_deadline || null,
      daysRemaining,
      setAsideCode: latestNotice.set_aside_code || null,
      setAsideDescription: latestNotice.set_aside_description || null,
    },

    codes: {
      naicsCode: latestNotice.naics_code || null,
      naicsIndustry: latestNotice.naics_industry || null,
      pscCode: latestNotice.psc_code || null,
      pscIndustry: latestNotice.psc_industry || null,
    },

    placeOfPerformance: {
      street: latestNotice.place_of_performance_street_address || null,
      city: latestNotice.place_of_performance_city || null,
      state: latestNotice.place_of_performance_state || null,
      zip: latestNotice.place_of_performance_zip || null,
      country: latestNotice.place_of_performance_country || null,
      formatted: formatPlaceOfPerformance(latestNotice),
    },

    contact: {
      title: latestNotice.primary_contact_title || null,
      name: latestNotice.primary_contact_full_name || null,
      email: latestNotice.primary_contact_email || null,
      phone: latestNotice.primary_contact_phone || null,
      fax: latestNotice.primary_contact_fax || null,
    },

    secondaryContact,

    timeline,
    notices: sortedNotices,
    latestNotice,
    awardNotices,

    description: latestNotice.description || null,
    aiSummary: contract.ai_summary || null,

    files,

    externalLink: latestNotice.link || null,
  }
}

/**
 * Normalize linked awards data from USASpending
 */
export function normalizeLinkedAwards(
  data: LinkedAwardsResponse | null | undefined,
): NormalizedLinkedAwardData | null {
  if (!data || !data.linked_awards || data.linked_awards.length === 0) {
    return null
  }

  const awards = data.linked_awards.map((award: LinkedAwardSummary) => ({
    id: award.award_id,
    generatedId: award.generated_unique_award_id,
    piid: award.piid || null,
    fain: award.fain || null,
    category: award.category || null,
    typeDescription: award.type_description || null,
    description: award.description || null,
    totalObligation: award.total_obligation ?? null,
    recipientName: award.recipient_name || null,
    recipientUei: award.recipient_uei || null,
    recipientHash: award.recipient_hash || null,
    recipientLevels: award.recipient_levels || [],
    parentUei: award.parent_uei || null,
    agencyName: award.awarding_agency_name || null,
    subtierName: award.awarding_subtier_name || null,
    actionDate: award.action_date || null,
    dateSigned: award.date_signed || null,
    startDate: award.period_of_performance_start_date || null,
    endDate: award.period_of_performance_current_end_date || null,
    popCity: award.pop_city || null,
    popState: award.pop_state || null,
    popCountry: award.pop_country || null,
  }))

  return {
    totalObligated: data.total_obligated ?? null,
    awardCount: awards.length,
    awards,
  }
}

/**
 * Add linked awards to an existing timeline as award events.
 * Returns a new sorted array without mutating the input.
 */
export function extendTimelineWithLinkedAwards(
  timeline: TimelineEvent[],
  linkedAwards: NormalizedLinkedAwardData | null,
  referenceMs: number,
): TimelineEvent[] {
  if (!linkedAwards || linkedAwards.awards.length === 0) {
    return timeline
  }

  const linkedAwardEvents: TimelineEvent[] = linkedAwards.awards
    .filter((award) => award.dateSigned || award.actionDate)
    .map((award) => {
      const date = (award.dateSigned || award.actionDate)!
      return {
        id: `linked-award-${award.id}`,
        date,
        label: award.recipientName
          ? `Awarded to ${award.recipientName}`
          : `Award ${award.piid || award.fain || award.id}`,
        status: getTimelineStatus(date, referenceMs),
        variant: 'award' as const,
      }
    })

  if (linkedAwardEvents.length === 0) {
    return timeline
  }

  // Create new array and sort chronologically (don't mutate input)
  const combined = [...timeline, ...linkedAwardEvents]
  combined.sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  )

  return combined
}

/**
 * Convert SAM.gov award notices to unified format
 */
export function samAwardNoticesToUnified(
  awardNotices: Array<{
    id: string
    awardNumber: string | null
    awardDate: string | null
    awardAmount: number | null
    awardee: string | null
  }>,
): UnifiedAwardNotice[] {
  return awardNotices.map((notice) => ({
    id: notice.id,
    source: 'sam' as const,
    awardNumber: notice.awardNumber,
    awardDate: notice.awardDate,
    awardAmount: notice.awardAmount,
    awardee: notice.awardee,
    generatedId: null,
    typeDescription: null,
    recipientHash: null,
    recipientLevels: [],
    parentUei: null,
    agencyName: null,
  }))
}

/**
 * Convert USAspending linked awards to unified award notice format
 */
export function linkedAwardsToUnified(
  linkedAwards: NormalizedLinkedAwardData | null,
): UnifiedAwardNotice[] {
  if (!linkedAwards || linkedAwards.awards.length === 0) {
    return []
  }

  return linkedAwards.awards.map((award) => ({
    id: `usaspending-${award.id}`,
    source: 'usaspending' as const,
    awardNumber: award.piid || award.fain || null,
    awardDate: award.dateSigned || award.actionDate,
    awardAmount: award.totalObligation,
    awardee: award.recipientName,
    generatedId: award.generatedId,
    typeDescription: award.typeDescription,
    recipientHash: award.recipientHash,
    recipientLevels: award.recipientLevels,
    parentUei: award.parentUei,
    agencyName: award.agencyName,
  }))
}

/**
 * Combine SAM.gov and USAspending awards into a unified list,
 * sorted by award date (newest first)
 */
export function combineAwardNotices(
  samAwardNotices: Array<{
    id: string
    awardNumber: string | null
    awardDate: string | null
    awardAmount: number | null
    awardee: string | null
  }>,
  linkedAwards: NormalizedLinkedAwardData | null,
): UnifiedAwardNotice[] {
  const samUnified = samAwardNoticesToUnified(samAwardNotices)
  const linkedUnified = linkedAwardsToUnified(linkedAwards)

  const combined = [...samUnified, ...linkedUnified]

  // Sort by award date descending (newest first), nulls last
  combined.sort((a, b) => {
    if (!a.awardDate && !b.awardDate) return 0
    if (!a.awardDate) return 1
    if (!b.awardDate) return -1
    return new Date(b.awardDate).getTime() - new Date(a.awardDate).getTime()
  })

  return combined
}
